import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, ExternalLink, X } from "lucide-react";
import { api, GcalTaskLinkStatus, Task } from "../lib/tauri";
import { useUiStore } from "../stores/uiStore";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Modal } from "./ui/Modal";

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultTime(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  return d.toTimeString().slice(0, 5);
}

function toIso(date: string, time: string, durationMin: number): { startIso: string; endIso: string } {
  const start = new Date(`${date}T${time}:00`);
  const end = new Date(start.getTime() + durationMin * 60_000);
  return {
    startIso: start.toISOString(),
    endIso: end.toISOString(),
  };
}

export function TaskCalendarDialog({
  task,
  open,
  onClose,
}: {
  task: Task;
  open: boolean;
  onClose: () => void;
}) {
  const [date, setDate] = useState(task.dueDate ?? todayKey());
  const [time, setTime] = useState(defaultTime());
  const [duration, setDuration] = useState(60);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [linkStatus, setLinkStatus] = useState<GcalTaskLinkStatus | null>(null);
  const [htmlLink, setHtmlLink] = useState<string | null>(null);
  const showToast = useUiStore((s) => s.showToast);

  useEffect(() => {
    if (!open) return;
    setDate(task.dueDate ?? todayKey());
    setTime(defaultTime());
    setDuration(60);
    setMessage("");
    setHtmlLink(null);
    void api.gcalTaskLinkStatus?.(task.id)
      .then(setLinkStatus)
      .catch(() => setLinkStatus(null));
  }, [open, task.dueDate, task.id]);

  const iso = useMemo(() => toIso(date, time, duration), [date, duration, time]);

  const submit = async () => {
    setBusy(true);
    setMessage("");
    try {
      // 非同期前にリンク状態を確定（setLinkStatus は即時反映されないため、
      // この変数を使わないとトースト文言がズレる）。
      const wasLinked = linkStatus?.linked ?? false;
      const result = await api.gcalExportTask!(task.id, iso.startIso, iso.endIso);
      setHtmlLink(result.htmlLink);
      setLinkStatus({ linked: true, eventId: result.eventId, calendarId: "primary" });
      showToast(wasLinked ? "カレンダー予定を更新しました" : "カレンダーに追加しました");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} className="md:max-w-[420px]">
      <div className="flex items-center justify-between border-b border-border px-5 py-4 dark:border-border-dark">
        <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold">
          <CalendarPlus className="h-4 w-4" />
          <span className="truncate">カレンダー</span>
        </h2>
        <button
          type="button"
          aria-label="閉じる"
          onClick={onClose}
          className="rounded-md p-1.5 text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4 p-5">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{task.title}</div>
          {linkStatus?.linked && (
            <div className="mt-1 text-xs text-muted dark:text-muted-dark">リンク済み</div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-medium text-muted dark:text-muted-dark">
            日付
            <Input className="mt-1" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="text-xs font-medium text-muted dark:text-muted-dark">
            開始
            <Input className="mt-1" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </label>
        </div>
        <label className="block text-xs font-medium text-muted dark:text-muted-dark">
          所要時間（分）
          <Input
            className="mt-1"
            type="number"
            min={15}
            step={15}
            value={duration}
            onChange={(e) => setDuration(Math.max(15, Number(e.target.value) || 60))}
          />
        </label>
        {message && <p className="break-words text-xs text-danger-600">{message}</p>}
        <div className="flex flex-wrap justify-end gap-2">
          {htmlLink && (
            <Button
              variant="outline"
              leftIcon={<ExternalLink className="h-4 w-4" />}
              onClick={() => window.open(htmlLink, "_blank", "noopener,noreferrer")}
            >
              開く
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            閉じる
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "保存中" : linkStatus?.linked ? "更新" : "追加"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
